// The markdown parser produces standard ProseMirror JSON, but some of the
// nodes it emits inline (most notably images) are block-level in Hudu's schema
// -- our Image node supports captions, so it cannot live inside a paragraph.
// Inserting that JSON as-is throws "Invalid content for node paragraph".
//
// This normalizer walks the parsed document and hoists any non-inline node out
// of a textblock (paragraph/heading), splitting the surrounding inline content
// into separate textblocks. Marks are stripped from hoisted block nodes since
// block nodes cannot carry inline marks.

function isInlineType(typeName, schema) {
  if (typeName === "text") return true
  const type = schema?.nodes?.[typeName]
  return type ? type.isInline : false
}

function isTextblockType(typeName, schema) {
  const type = schema?.nodes?.[typeName]
  return type ? type.isTextblock : false
}

function normalizeNode(node, schema) {
  if (!node || node.type === "text" || !Array.isArray(node.content)) {
    return [node]
  }

  const children = node.content.flatMap((child) => normalizeNode(child, schema))

  if (!isTextblockType(node.type, schema)) {
    return [{ ...node, content: children }]
  }

  const output = []
  let inlineRun = []

  const flushRun = () => {
    if (inlineRun.length > 0) {
      output.push({ ...node, content: inlineRun })
      inlineRun = []
    }
  }

  children.forEach((child) => {
    if (isInlineType(child.type, schema)) {
      inlineRun.push(child)
      return
    }

    flushRun()
    // Strip marks: a block node hoisted out of a textblock cannot keep the
    // inline marks it inherited (e.g. an image wrapped in a link).
    const { marks: _marks, ...blockNode } = child
    output.push(blockNode)
  })

  flushRun()

  return output.length > 0 ? output : [{ ...node, content: [] }]
}

// Normalize a parsed markdown document (a `doc` JSON node) so it is valid for
// the editor schema. Returns the normalized document node.
export function normalizeMarkdownDocument(doc, schema) {
  if (!doc || !schema) return doc

  return normalizeNode(doc, schema)[0] ?? doc
}

export default normalizeMarkdownDocument
